import { useMemo } from 'react';
import { UseQueryOptions, useQueries } from 'react-query';
import { useEvent, client } from '../../';
import useSpeakers from '../../speakers';
import useSponsors from '../../sponsors';
import useEventSettings from '../../eventSettings';
import { Activity, SessionActivity, useActivities } from '../';
import { ActivitiesResponseType, hydrateActivity, Session } from '../useActivities';

const fetchSession = async (eventId: number, sessionId: number) => {
    const response = await client().get(`/api/v2/agenda/events/${eventId}/sessions/${sessionId}`);
    return response.data as Session;
};

type UseActivity = (
    args: { eventUniqueName: string; activityId: number },
    options?: UseQueryOptions<ActivitiesResponseType>
) => {
    activity: Activity | SessionActivity | null;
    isLoading: boolean;
    error?: Error;
};

const useActivity: UseActivity = (
    { eventUniqueName, activityId },
    options?: UseQueryOptions<ActivitiesResponseType>
) => {
    const activitiesQuery = useActivities(eventUniqueName, null, options);
    const activity = useMemo(
        () =>
            activitiesQuery.data
                ? activitiesQuery.data.find(activity => activity.id === activityId) ?? null
                : null,
        [activitiesQuery.data, activityId]
    );

    const shouldReturnUnlistedActivity =
        activity === null && activitiesQuery.isSuccess && Number.isInteger(activityId);
    const event = useEvent(eventUniqueName);
    const eventId = event?.data?.id;

    const speakersQuery = useSpeakers(eventId);
    const sponsorsQuery = useSponsors(eventId);
    const eventSettings = useEventSettings(eventUniqueName);

    const queries = shouldReturnUnlistedActivity
        ? [
              {
                  queryKey: ['unlisted_activity', activityId],
                  queryFn: async () => {
                      const eventId = event?.data?.id;
                      const unlistedActivity = await fetchSession(eventId, activityId);
                      return hydrateActivity(
                          unlistedActivity,
                          speakersQuery.data,
                          sponsorsQuery.data,
                          event.data?.timeZone,
                          eventSettings.data.locations,
                          eventId
                      );
                  },
                  staleTime: 1000 * 60 * 10, // 10 minutes in ms
                  enabled:
                      speakersQuery.isSuccess &&
                      sponsorsQuery.isSuccess &&
                      eventSettings.isSuccess &&
                      !!event.data?.timeZone,
              },
          ]
        : [];

    const [unlistedActivityQuery] = useQueries(queries);

    const unlistedActivity = {
        activity: unlistedActivityQuery?.data,
        isLoading: unlistedActivityQuery?.isLoading || unlistedActivityQuery?.isIdle,
        error: unlistedActivityQuery?.error as Error,
    };

    const regularActivity = {
        activity,
        isLoading: activitiesQuery.isLoading || activitiesQuery.isIdle,
        error: activitiesQuery.error,
    };

    return shouldReturnUnlistedActivity ? unlistedActivity : regularActivity;
};

export default useActivity;
